import React, { useState, useEffect } from "react";
import { 
  Box, 
  Flex, 
  Text, 
  Center, 
  Spinner, 
  Button, 
  HStack, 
  Icon, 
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { FaCheck, FaCog, FaTimes } from "react-icons/fa";
import { Header } from "build-ts/metadata";
import { MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import ListAgents from "components/agents/ListAgents";
import AgentForm from "components/agents/AgentForm";
import showToast from "hooks/useCustomToast";
import { v4 as uuidv4 } from "uuid";
import { getCustomAgent, listCustomAgents, putCustomAgent } from "services/CustomAgentServices";
import { CustomAgentProto } from "build-ts/custom_agent";
import { getScheduledJob, runJobAsync } from "services/ScheduleServices";
import { ScheduledJobProto } from "build-ts/schedule";
import JobRuns from "containers/schedule/JobRuns";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaHistory } from "react-icons/fa";
import { IoIosInfinite } from "react-icons/io";

const Agents: React.FC = () => {
  const [agentHeaders, setAgentHeaders] = useState<Header[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Header | null>(null);
  const [selectedAgentData, setSelectedAgentData] = useState<CustomAgentProto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAgentDetails, setIsLoadingAgentDetails] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [scheduledJobData, setScheduledJobData] = useState<ScheduledJobProto | null>(null);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [isDiscardChanges, setIsDiscardChanges] = useState(false);
  const [isUpdateAgent, setIsUpdateAgent] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  
  // Tab state management
  const [tabIndex, setTabIndex] = useState(0);

  // Fetch all agents on component mount
  useEffect(() => {
    const fetchAgents = async () => {
      setIsLoading(true);
      try {
        const agents = await listCustomAgents(
          -1, // all jobs
          1, // page 1
          "LAST_MODIFIED", // sort by last modified
          false, // sort ascending
          true, // includeScheduleActivity
          "" // activeSchedule
        );
        if (agents) {
          setAgentHeaders(agents?.headers || []);
        }
      } catch (error) {
        console.error("Error fetching agents:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgents();
  }, []);

  // Handle agent selection for editing
  const handleAgentSelection = async (agent: Header, preloadedAgentData?: CustomAgentProto | null) => {
    setSelectedAgent(agent);
    setIsCreatingNew(false);
    
    // Update URL to include agent ID
    if (agent?.id) {
      navigate(`/agent/${agent.id}`, { replace: true });
    }
    // Switch to Config tab if currently on Run tab
    if (tabIndex === 1) {
      setTabIndex(0);
    }
    
    // If we have preloaded data (e.g., from cloning), use it directly
    if (preloadedAgentData) {
      setSelectedAgentData(preloadedAgentData);
      return;
    }
    
    // Only fetch agent data if we don't already have it or it's a different agent
    if (!selectedAgentData || selectedAgentData.header?.id !== agent.id) {
      setIsLoadingAgentDetails(true);
      try {
        if (agent?.id) {
          const agentData = await getCustomAgent(agent?.id);
          setSelectedAgentData(agentData || null);
        }
      } catch (error) {
        console.error("Error fetching agent details:", error);
        setSelectedAgentData(null);
      } finally {
        setIsLoadingAgentDetails(false);
      }
    } else if (selectedAgentData && selectedAgentData.header?.id === agent.id) {
      // If it's the same agent but the header might have been updated (e.g., name change),
      // update the selectedAgentData with the new header information
      setSelectedAgentData({
        ...selectedAgentData,
        header: agent
      });
    }
  };

  // Handle Update Agent
  const handleUpdateAgent = () => {
    setIsUpdateAgent(true);
  };

  // Handle Discard Changes
  const handleDiscardChanges = () => {
    setIsDiscardChanges(true);
  };

  // Handle agent loading from URL parameter
  useEffect(() => {
    const loadAgent = async () => {
      if (id && agentHeaders.length > 0 && !selectedAgent) {
        // Handle loading existing agent from URL
        const agent = agentHeaders.find(agent => agent.id === id);
        if (agent) {
          setSelectedAgent(agent);
          setIsLoadingAgentDetails(true);
          setIsCreatingNew(false);
          try {
            if(agent?.id) {
              const agentData = await getCustomAgent(agent?.id);
              setSelectedAgentData(agentData || null);
            }
          } catch (error) {
            console.error("Error fetching agent details:", error);
            setSelectedAgentData(null);
          } finally {
            setIsLoadingAgentDetails(false);
          }
        }
      }
    };
    loadAgent();
  }, [id, agentHeaders, selectedAgent]);

  // Handle agent runs tab switching
  useEffect(() => {
    if (location.pathname.includes("/runs")) {
      setTabIndex(1);
      if(selectedAgent?.id) {
        handleShowAgentRuns();
      }
    } else {
      setTabIndex(0);
    }
  }, [location.pathname, selectedAgent]);

  // Handle creating a new agent
  const handleCreateNewAgent = async () => {
    setSelectedAgent(null);
    setSelectedAgentData(null);
    setIsCreatingNew(true);
    setTabIndex(0); // Ensure we're on Config tab
    setScheduledJobData(null); // Clear any previous run data
    navigate("/agent");
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedAgent(null);
    setSelectedAgentData(null);
    setIsCreatingNew(false);
    setTabIndex(0); // Reset to Config tab
    setScheduledJobData(null); // Clear any run data
    navigate("/agent");
  };

  // Handle form submission (create or update)
  const handleFormSubmit = async (updatedAgent: CustomAgentProto) => {
    setIsSaving(true);
    try {
      // Ensure we have proper IDs and metadata
      const agentToSave = {
        ...updatedAgent,
        header: {
          ...updatedAgent.header,
          id: selectedAgentData?.header?.id || uuidv4(),
          type: MetadataTypeEnum.CUSTOM_AGENT,
          created_date_ms: selectedAgentData?.header?.created_date_ms || Date.now(),
          last_modified_date_ms: Date.now(),
          last_modified_by: "current_user", // TODO: Get from user context
        },
      };

      const result = await putCustomAgent(agentToSave);
      
      if (result) {
        showToast("Agent saved successfully", "", "success");
        
        // Update the agents list
        if (selectedAgentData) {
          // Update existing agent
          setAgentHeaders(prev => 
            prev.map(agent => 
              agent.id === result.header?.id ? result.header! : agent
            )
          );
        } else {
          // Add new agent to top of list
          if (result.header) {
            setAgentHeaders(prev => [result.header!, ...prev]);
          }
        }
        
        // Set the saved agent as selected and update the form data
        setSelectedAgent(result.header!);
        setSelectedAgentData(result);
        setIsCreatingNew(false);
        
        // Navigate to the saved agent's URL
        if (result.header?.id) {
          navigate(`/agent/${result.header.id}`, { replace: true });
        }
      }
    } catch (error) {
      showToast("Failed to save agent", "", "error");
      console.error("Error saving agent:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle form cancellation
  const handleFormCancel = () => {
    handleBackToList();
  };

  // Handle run job
  const handleRunJob = async () => {
    if (!selectedAgent || !selectedAgent.id) return;
    await runJobAsync(selectedAgent?.id, metadataTypeEnumToJSON(MetadataTypeEnum.CUSTOM_AGENT));
    showToast(
      "Job is running",
      "The scheduled job has been triggered and is now running.",
      "success"
    );
  };

  // Handle show agent runs
  const handleShowAgentRuns = async () => {
    if (!selectedAgent || !selectedAgent.id) return;
    
    setIsLoadingRuns(true);
    try {
      const scheduledJob = await getScheduledJob(selectedAgent.id, true);
      setScheduledJobData(scheduledJob || null);
    } catch (error) {
      console.error("Error fetching agent runs:", error);
      showToast("Failed to load agent runs", "", "error");
    } finally {
      setIsLoadingRuns(false);
    }
  };

  // Handle run deleted callback
  const handleRunDeleted = (runId: string) => {
    // Refresh the scheduled job data after a run is deleted
    if (selectedAgent?.id) {
      handleShowAgentRuns();
    }
  };

  // Handle tab change
  const handleTabChange = (index: number) => {
    setTabIndex(index);
    if (index === 1) {
      // Navigate to runs URL and load data
      navigate(`/agent/${id}/runs`);
    } else {
      // Navigate back to agent config URL
      navigate(`/agent/${id}`);
    }
  };

  const renderMainContent = () => {
    if (selectedAgentData || isCreatingNew) {
      return (
        <Box height="100%" bg="white">
          <Tabs 
            colorScheme="purple" 
            isFitted 
            variant="line" 
            size="md"
            height="100%"
            defaultIndex={0}
            index={tabIndex}
            onChange={handleTabChange}
          >
            <TabList>
              <Tab isDisabled={!selectedAgent || !selectedAgent.id}    
              _hover={{bg: "purple.50"}}
              >
                <Icon as={FaCog} boxSize={4} color="fg.default" ml={2} mb={1} />
                <Text fontSize="md" fontWeight="600" color="fg.default" mt={0} textAlign="left" ml={2} mb={1}>
                  Configuration
                </Text>
              </Tab>
              <Tab isDisabled={!selectedAgent || !selectedAgent.id || isCreatingNew} 
                _hover={{bg: "purple.50"}}
                >
                <Icon as={FaHistory} boxSize={4} color="fg.default" ml={2} mb={1} />
                <Text fontSize="md" fontWeight="600" color="fg.default" mt={0} textAlign="left" ml={2} mb={1}>
                  Past Runs
                </Text>
              </Tab>
              <Button
                variant="outline"
                onClick={handleRunJob}
                isDisabled={!selectedAgent || !selectedAgent.id || isCreatingNew}
                size="sm"
                _hover={{
                  bg: "purple.700",
                  color: "white",
                  fontWeight: "700",
                }}
                leftIcon={<Icon as={IoIosInfinite} boxSize={6}/>}
                fontWeight="600"
                alignSelf="center"
                mr={2}
                ml={2}
                borderWidth="2px"
                borderColor="purple.700"
                color="purple.700"
                fontSize="md"
                cursor="pointer"
              >
                Run
              </Button>
            </TabList>
            
            <TabPanels height="calc(100vh - 120px)">
              <TabPanel p={0} height="100%">
                <Box height="100%">                  
                  {/* Form Content */}
                  <Box height="calc(100% - 60px)" p={2} overflowY="auto">
                    {isLoadingAgentDetails ? (
                      <Center height="100%">
                        <Spinner size="lg" />
                        <Text ml={4}>Loading agent details...</Text>
                      </Center>
                    ) : (
                      <AgentForm
                        initialData={selectedAgentData || undefined}
                        onSubmit={handleFormSubmit}
                        onCancel={handleFormCancel}
                        isLoading={isSaving}
                        setHasFormChanges={setHasFormChanges}
                        isCreatingNew={isCreatingNew}
                        isUpdateAgent={isUpdateAgent}
                        setIsUpdateAgent={setIsUpdateAgent}
                        isDiscardChanges={isDiscardChanges}
                        setIsDiscardChanges={setIsDiscardChanges}
                      />
                    )}
                  </Box>
                </Box>
              </TabPanel>
              
              <TabPanel p={2} height="100%">
                <Box height="100%">
                  {/* Render JobRuns component */}
                  {isLoadingRuns ? (
                    <Center height="100%">
                      <Spinner size="lg" />
                      <Text ml={4}>Loading agent runs...</Text>
                    </Center>
                  ) : scheduledJobData ? (
                    <JobRuns 
                      scheduledJob={scheduledJobData} 
                      scheduleJobRuns={scheduledJobData.job_runs || []} 
                      onRunDeleted={handleRunDeleted}
                    />
                  ) : (
                    <Center height="100%" bg="white">
                      <Text color="gray.500" fontSize="lg">
                        No agent runs available
                      </Text>
                    </Center>
                  )}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>

           {/* Bottom Overlay for Form Changes - Right-aligned and limited width */}
           {hasFormChanges && !isCreatingNew && (
            <Box
              position="fixed"
              bottom="20px"
              right="20px"
              bg="white"
              border="1px"
              borderColor="gray.200"
              borderRadius="lg"
              boxShadow="lg"
              p={4}
              zIndex={1000}
              minW="300px"
              maxW="400px"
            >
              <HStack spacing={3} justify="space-around">
              <Button 
                  colorScheme="green" 
                  size="md"
                  onClick={handleUpdateAgent}
                  isLoading={isSaving}
                  loadingText="Updating..."
                  leftIcon={<Icon as={FaCheck} boxSize={3} />}
                >
                  Update
                </Button>
                <Button 
                  colorScheme="red" 
                  variant="outline"
                  size="md"
                  onClick={handleDiscardChanges}
                  isDisabled={isSaving}
                  leftIcon={<Icon as={FaTimes} boxSize={3}/>}
                >
                  Discard Changes
                </Button>
              </HStack>
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Center height="100%" bg="white">
        <Text color="gray.500" fontSize="lg">
          Select an agent from the list to edit or click + to create new
        </Text>
      </Center>
    );
  };

  return (
    <Box width="100%" height="100vh" bg="gray.50">
      <Flex direction="row" height="100%">
        {/* Agent List - 1/3 width */}
        <Box width="27%" minWidth="350px" height="100%">
          <ListAgents
            agentHeaders={agentHeaders}
            setAgentHeaders={setAgentHeaders}
            selectedAgent={selectedAgent ? [selectedAgent] : []}
            onAgentSelection={handleAgentSelection}
            onCreateNewAgent={handleCreateNewAgent}
            isLoading={isLoading}
          />
        </Box>

        {/* Main Content - 2/3 width */}
        <Box width="73%" height="100%">
          {renderMainContent()}
        </Box>
      </Flex>
    </Box>
  );
};

export default Agents;